/**
 * Why a server-owned queue paused, in words a member reads after "Queue
 * paused: ". The keys are daemon pause reasons (src/queue-driver.ts
 * pauseReasonFor, the reservation refusals, and the account-wide codes that
 * pause every queue the member has), which are NOT ChatErrorCodes and are not
 * part of the errorMessages.ts mirror. An unknown reason falls back rather than
 * showing a code. There is no `archived` entry: archiving a conversation clears
 * its queue instead of pausing it.
 */
const PAUSE_COPY: Record<string, string> = {
  stopped: "you pressed Stop",
  restart: "the server restarted",
  // Members never learn that usage is metered in money: the same wording as
  // errorMessages.ts gives the member for these codes.
  daily_cap: "you reached today's usage limit",
  monthly_cap: "you reached this month's usage limit",
  spend_reserved: "your other chats are still working",
  credits_exhausted: "ThunderAy AI is temporarily unavailable, so every queued chat is waiting",
  rate_limited: "the AI service is rate limiting, so every queued chat is waiting",
  overloaded: "the AI service is overloaded, so every queued chat is waiting",
  timeout: "the last answer timed out",
  interrupted: "the last answer was cut off",
  // The settlement's status when it carried no code (pauseReasonFor).
  error: "the last answer failed",
  empty_completion: "the last answer came back empty",
  unexpected_result: "the last answer ended unexpectedly",
  deep_mode_limit: "the last answer hit its work limit",
  context_exhausted: "this chat is too long; start a new one",
  worktree_failed: "a working branch could not be prepared",
  subagent_write_blocked: "a sub-agent tried to change files, so the answer was stopped",
  subagent_guard_failed: "the sub-agent safety rule could not be enforced",
  persist_failed: "a prompt could not be saved",
  spawn_timeout: "the next prompt could not start in time",
  spawn_failed: "the next prompt could not start",
  image_read_failed: "an image in the next prompt is missing",
  reclaimed: "another message took over this conversation",
  start_threw: "the next prompt could not start",
  // A synchronous throw in the queue driver before the user turn was written
  // (src/auto-resume.ts UnattendedResult). Nothing ran, so it reads like a
  // start that failed.
  internal: "the next prompt could not start",
};

const FALLBACK = "the last answer did not finish cleanly";

export function pauseReasonText(reason: string | null): string {
  if (reason === null) return FALLBACK;
  return Object.prototype.hasOwnProperty.call(PAUSE_COPY, reason) ? PAUSE_COPY[reason]! : FALLBACK;
}
